import { Box, Modal } from "@mui/material";
import Text from "components/atoms/text";
import { AccessbilityViewHeight, useNav } from "context/navContext";
import { useEffect, useRef, useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import isJFK, { isTab } from "utils/helperFunctions";
import { XIconBlack } from "utils/iconList";
import { UserContext } from "context/userContext";
import { CartContext } from "context/cartContext";
import { DeliveryOptionContext } from "context/deliveryOptionContext";
import { FoodTypeContext } from "context/foodTypeContext";
import { removeSessionStorage } from "utils/storageUtil";
import { cancelCheckout } from "utils/checkoutKey";

const InactivityHandler = () => {
  const { accessibilityMode, setAccessibilityMode, excludedInActivityRoutes } =
    useNav();
  const history = useHistory();
  const location = useLocation();
  const useUser = useContext(UserContext);
  const useCart = useContext(CartContext);
  const useDelivery = useContext(DeliveryOptionContext);
  const useFoodType = useContext(FoodTypeContext);

  const [showModal, setShowModal] = useState(false);
  const inactivityTimerRef = useRef(null);
  const modalTimerRef = useRef(null);

  const startInactivityTimer = () => {
    clearTimeout(inactivityTimerRef.current);
    if (!showModal) {
      inactivityTimerRef.current = setTimeout(() => {
        setShowModal(true);
      }, 90000); // 90 seconds of inactivity
    }
  };

  const clearModalCountdown = () => {
    clearTimeout(modalTimerRef.current);
  };

  const resetAllTimers = () => {
    clearTimeout(inactivityTimerRef.current);
    clearModalCountdown();
  };

  const redirectToHomepage = async () => {
    console.log("Redirecting to homepage");
    resetAllTimers();
    setShowModal(false);
    removeSessionStorage("isAgeDeclared");
    removeSessionStorage("paymentContent");

    await cancelCheckout(null, "inactivity timeout");
    await useUser.onLogout();
    useCart.reset();
    useDelivery.resetDeliverySelection();
    useFoodType.resetFoodType();
    setAccessibilityMode(false);
    window.location.replace("/");
  };

  const handleUserResponse = (response) => {
    if (response === "yes") {
      resetAllTimers();
      setShowModal(false);
      startInactivityTimer();
    } else {
      redirectToHomepage();
    }
  };

  useEffect(() => {
    if (
      excludedInActivityRoutes.includes(location.pathname) ||
      location.pathname.includes("payment")
    ) {
      return;
    }

    const events = ["click", "scroll", "keydown"];

    const handleUserActivity = () => {
      startInactivityTimer();
    };

    events.forEach((event) =>
      window.addEventListener(event, handleUserActivity)
    );

    startInactivityTimer();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleUserActivity)
      );
      resetAllTimers();
    };
  }, [showModal, location, excludedInActivityRoutes]);

  useEffect(() => {
    if (showModal) {
      modalTimerRef.current = setTimeout(() => {
        redirectToHomepage();
      }, 30000); // 30 seconds after modal appears
    } else {
      clearTimeout(modalTimerRef.current);
    }

    return () => {
      clearTimeout(modalTimerRef.current);
    };
  }, [showModal]);

  const style = {
    position: "absolute",
    top: accessibilityMode ? "80vh" : "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isTab() ? "calc(100% - 60px)" : "954px",
    maxHeight: accessibilityMode ? AccessbilityViewHeight : "unset",
    overflowY: accessibilityMode ? "auto" : "unset",
    bgcolor: "background.paper",
    border: "none",
    boxShadow: 24,
    borderRadius: 3,
  };

  return (
    <Modal open={showModal} onClose={() => handleUserResponse("yes")}>
      <Box sx={style}>
        <ModalCloseIconContainer>
          <CloseIcon
            alt="close"
            src={XIconBlack}
            onClick={() => handleUserResponse("yes")}
          />
        </ModalCloseIconContainer>
        <ContentWrapper>
          <TitleTextWrapper>
            <Text className="font-bold" variant="header">
              Are you still there?
            </Text>
          </TitleTextWrapper>
          <DescriptionTextWrapper>
            <Text>
              It looks like you've been inactive. Please confirm if you'd like
              to continue. This session will end automatically if there's no
              response.
            </Text>
          </DescriptionTextWrapper>
          <ButtonRow>
            <EndButton
              onClick={() => handleUserResponse("no")}
              style={{
                borderRadius: isJFK() ? "0px" : "12px",
                color: isJFK()
                  ? "var(--primary-foreground)"
                  : "var(--secondary-foreground)",
              }}
            >
              <Text>End Session</Text>
            </EndButton>
            <ContinueButton
              onClick={() => handleUserResponse("yes")}
              style={{
                borderRadius: isJFK() ? "0px" : "12px",
              }}
            >
              <Text>Continue Ordering</Text>
            </ContinueButton>
          </ButtonRow>
        </ContentWrapper>
      </Box>
    </Modal>
  );
};

const ModalCloseIconContainer = styled.div`
  position: absolute;
  top: 32px;
  right: 32px;
  cursor: pointer;
  z-index: 9;
`;

const CloseIcon = styled.img`
  width: 32px;
  height: 32px;
`;

const ContentWrapper = styled.div`
  padding: 32px 32px;
`;

const TitleTextWrapper = styled.div`
  font-weight: 700;
  font-size: 32px;
  color: var(--text-black);
  line-height: 130%;
  letter-spacing: 0em;
`;

const DescriptionTextWrapper = styled.div`
  font-size: 28px;
  color: var(--text-black);
  line-height: 130%;
  letter-spacing: 0em;
  padding-top: 32px;
`;

const ButtonRow = styled.div`
  margin-top: 48px;
  display: flex;
  justify-content: space-around;
  gap: 20px;
`;

const EndButton = styled.button`
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;

  background-color: var(--secondary);
  border: 2px solid var(--primary);

  font-size: 30px;
  line-height: 130%;
  letter-spacing: 0em;
`;

const ContinueButton = styled.button`
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;

  background-color: var(--primary);
  border: 2px solid var(--primary);

  font-size: 30px;
  color: var(--primary-foreground);
  line-height: 130%;
  letter-spacing: 0em;
`;

export default InactivityHandler;
